<template>
    <div class="content-wrapper">
        <app-breadcrumb :page-title="pageTitle" :icon="pageIcon"/>
        <div class="row" v-if="$can('view_posts_overview') || $can('view_reaction_overview')">
            <div class="col-xl-8 mb-primary" v-if="$can('view_posts_overview')">
                <div class="card card-with-shadow border-0 h-100">
                    <div
                        class="card-header bg-transparent p-primary d-flex justify-content-between align-items-center"
                    >
                        <h5 class="card-title mb-0">{{ $t("posts_overview") }}</h5>
                        <ul class="nav tab-filter-menu justify-content-flex-end">
                            <li
                                class="nav-item"
                                v-for="(item, index) in chartFilterOptions"
                                :key="index"
                            >
                                <a
                                    href="#"
                                    class="nav-link py-0"
                                    :class="[
                                        postsFilter == item.id
                                        ? 'active'
                                        : index === 0 && postsFilter === ''
                                        ? 'active'
                                        : '',
                                    ]"
                                    @click.prevent="postsFilterValue(item.id)"
                                >
                                    {{ item.value }}
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="card-body min-height-300">
                        <app-overlay-loader v-if="postOverviewPreloader"/>
                        <template v-else>
                            <app-chart
                                class="mb-primary"
                                type="custom-line-chart"
                                :height="230"
                                :labels="lineChartLabels"
                                :data-sets="lineChartData"
                            />

                            <div class="chart-data-list d-flex flex-wrap justify-content-center">
                                <div class="data-group-item" style="color: #4466f2">
                                    <span class="square" style="background-color: #4466f2"/>
                                    {{ $t("public") }}

                                    <span class="value">{{ publicPost }}</span>
                                </div>
                                <div class="data-group-item" style="color: #27ae60">
                                    <span class="square" style="background-color: #27ae60"/>
                                    {{ $t("shared_with_team") }}

                                    <span class="value">{{ teamPost }}</span>
                                </div>
                                <div class="data-group-item" style="color: #a45ffd">
                                    <span class="square" style="background-color: #a45ffd"/>
                                    {{ $t("total") }}

                                    <span class="value">{{ totalPost }}</span>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div class="col-xl-4 mb-primary" v-if="$can('view_reaction_overview')">
                <app-overlay-loader v-if="reactionsPreloader"/>
                <div class="card card-with-shadow border-0 h-100" v-else>
                    <div
                        class="card-header bg-transparent p-primary d-flex justify-content-between align-items-center"
                    >
                        <h5 class="card-title mb-0">{{ $t("total_reactions") }}</h5>
                        <div class="badge dashboard-badge badge-pill text-capitalize">
                            {{ reactions.total }}
                        </div>
                    </div>
                    <div class="card-body">
                        <app-chart
                            class="mb-primary"
                            type="dough-chart"
                            :height="230"
                            :labels="reactions.labels"
                            :data-sets="reactions.dataset"
                        />
                        <div class="chart-data-list">
                            <div class="d-flex justify-content-center">
                                <div v-for="(item, index) in reactions.elements" :key="index">
                                    <div class="data-group-item" :style="item.color">
                                        <span class="square" :style="item.background_color"/>
                                        {{ item.key }}
                                        <span class="value">{{ item.value }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="$can('view_user_summary') || $can('view_post_summary') || $can('view_top_3_post_creator')">
            <div class="col-xl-4 mb-primary" v-if="$can('view_user_summary')">
                <app-overlay-loader v-if="userCardPreloader"/>
                <div class="card card-with-shadow border-0" v-else>
                    <div
                        class="card-header bg-transparent p-primary d-flex justify-content-between align-items-center"
                    >
                        <h5 class="card-title mb-0">
                            {{ $t("total_user") }}
                        </h5>
                        <div class="badge dashboard-badge badge-pill text-capitalize">
                            {{ userCard.total_user }}
                        </div>
                    </div>
                    <div class="card-body p-primary">
                        <div
                            class="dashboard-widgets dashboard-icon-widget pb-primary"
                        >
                            <div class="icon-wrapper">
                                <app-icon key="user-check" name="user-check"/>
                            </div>
                            <div class="widget-data">
                                <h6>{{ userCard.total_active }}</h6>
                                <p>{{ $t("active_user") }}</p>
                            </div>
                        </div>
                        <div
                            class="dashboard-widgets dashboard-icon-widget pb-primary"
                        >
                            <div class="icon-wrapper">
                                <app-icon key="user-x" name="user-check"/>
                            </div>
                            <div class="widget-data">
                                <h6>{{ userCard.total_de_active }}</h6>
                                <p>{{ $t("de_active_user") }}</p>
                            </div>
                        </div>
                        <div
                            class="dashboard-widgets dashboard-icon-widget pb-primary"
                        >
                            <div class="icon-wrapper">
                                <app-icon key="user-plus" name="user-check"/>
                            </div>
                            <div class="widget-data">
                                <h6>{{ userCard.total_invited }}</h6>
                                <p>{{ $t("invited_user") }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-4 mb-primary" v-if="$can('view_post_summary')">
                <app-overlay-loader v-if="userCardPreloader"/>
                <div class="card card-with-shadow border-0" v-else>
                    <div
                        class="card-header bg-transparent p-primary d-flex justify-content-between align-items-center"
                    >
                        <h5 class="card-title mb-0">
                            {{ $t("total_post") }}
                        </h5>
                        <div class="badge dashboard-badge badge-pill text-capitalize">
                            {{ postCard.total_post }}
                        </div>
                    </div>
                    <div class="card-body p-primary">
                        <div
                            class="dashboard-widgets dashboard-icon-widget pb-primary"
                        >
                            <div class="icon-wrapper">
                                <app-icon key="user-check" name="user-check"/>
                            </div>
                            <div class="widget-data">
                                <h6>{{ postCard.total_reaction }}</h6>
                                <p>{{ $t("total_reaction") }}</p>
                            </div>
                        </div>
                        <div
                            class="dashboard-widgets dashboard-icon-widget pb-primary"
                        >
                            <div class="icon-wrapper">
                                <app-icon key="user-x" name="user-check"/>
                            </div>
                            <div class="widget-data">
                                <h6>{{ postCard.total_comment }}</h6>
                                <p>{{ $t("total_comment") }}</p>
                            </div>
                        </div>
                        <div
                            class="dashboard-widgets dashboard-icon-widget pb-primary"
                        >
                            <div class="icon-wrapper">
                                <app-icon key="user-plus" name="user-check"/>
                            </div>
                            <div class="widget-data">
                                <h6>{{ postCard.total_reply }}</h6>
                                <p>{{ $t("total_reply") }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-4" v-if="$can('view_top_3_post_creator')">
                <app-overlay-loader v-if="topPosterPreloader"/>
                <div class="card card-with-shadow border-0" v-else>
                    <div
                        class="card-header bg-transparent p-primary d-flex justify-content-between align-items-center"
                    >
                        <h5 class="card-title mb-0">{{ $t("top_three_post_creator") }}</h5>
                    </div>
                    <div class="card-body min-height-315">
                        <app-chart
                            type="bar-chart"
                            :height="260"
                            :labels="topPoster.label"
                            :data-sets="topPoster.data"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {axiosGet} from "../../helpers/AxiosHelper";

export default {
    data() {
        return {
            pageTitle: 'Dashboard',
            pageIcon: 'settings',
            userCardPreloader: false,
            postCardPreloader: false,
            topPosterPreloader: false,
            reactionsPreloader: false,
            postOverviewPreloader: false,
            userCard: {},
            postCard: {},
            // posts Overview - line chart
            chartFilterOptions: [
                { id: "last_seven_days", value: this.$t("last_seven_days") },
                { id: "this_week", value: this.$t("this_week") },
                { id: "last_week", value: this.$t("last_week") },
                { id: "this_month", value: this.$t("this_month") },
                { id: "last_month", value: this.$t("last_month") },
                { id: "this_year", value: this.$t("this_year") },
                { id: "total", value: this.$t("total") },
            ],
            postsFilter: "last_seven_days",
            lineChartLabels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
            lineChartData: [
                {
                    title: this.$t("public"),
                    fill: false,
                    borderWidth: 1.5,
                    borderColor: "#4466F2",
                    backgroundColor: "#4466F2",
                    data: [20, 30, 40, 50, 60, 20, 50],
                },
                {
                    title: this.$t("shared_with_team"),
                    fill: false,
                    borderWidth: 1.5,
                    borderColor: "#27AE60",
                    backgroundColor: "#27AE60",
                    data: [68, 57, 41, 66, 43, 59, 51],
                },
                {
                    title: this.$t("total"),
                    fill: false,
                    borderWidth: 1.5,
                    borderColor: "#A45FFD",
                    backgroundColor: "#A45FFD",
                    data: [10, 30, 30, 25, 10, 5, 6],
                },
            ],
            publicPost: 270,
            teamPost: 328,
            totalPost: 598,

            // Top three poster - bar chart
            topPoster: {
                label: [],
                data: [
                    {
                        label: "Data",
                        backgroundColor: "#4466F2",
                        barThickness: 15,
                        data: [],
                        borderWidth: 0,
                    },
                ],
            },
            // reaction - dough-chart
            reactions: {
                labels: [this.$t("post"), this.$t("comment"), this.$t("reply")],
                dataset: [
                    {
                        backgroundColor: ["#4466F2", "#27AE60", "#FC5710"],
                        data: [],
                        borderWidth: 0,
                    },
                ],
                elements: [
                    {
                        key: this.$t("post"),
                        value: 10,
                        background_color: "background-color: #4466F2;",
                        color: "color: #4466F2;",
                    },
                    {
                        key: this.$t("comment"),
                        value: 15,
                        background_color: "background-color: #27AE60;",
                        color: "color: #27AE60;",
                    },
                    {
                        key: this.$t("reply"),
                        value: 20,
                        background_color: "background-color: #FC5710;",
                        color: "color: #FC5710;",
                    },
                ],
                total: 0,
            },
        }
    },
    methods: {
        getUserCard() {
            this.userCardPreloader = true;
            axiosGet(`user-card`).then(({data}) => {
                this.userCard = data;
            }).finally(() => this.userCardPreloader = false)
        },
        getPostCard() {
            this.postCardPreloader = true;
            axiosGet(`post-card`).then(({data}) => {
                this.postCard = data;
            }).finally(() => this.postCardPreloader = false)
        },
        getTopPoster() {
            this.topPosterPreloader = true;
            axiosGet(`top-poster`).then(({data}) => {
                this.topPoster.label = data.label
                this.topPoster.data[0].data = data.posts
            }).finally(() => this.topPosterPreloader = false)
        },
        getReactions() {
            this.reactionsPreloader = true;
            axiosGet(`all-reactions`).then(({data}) => {
                this.reactions.dataset[0].data = data.dataset
                this.reactions.elements[0].value = data.post
                this.reactions.elements[1].value = data.comment
                this.reactions.elements[2].value = data.reply
                this.reactions.total = data.total
            }).finally(() => this.reactionsPreloader = false)
        },

        postsFilterValue(value) {
            this.postsFilter = value;
            this.postOverviewPreloader = true;
            axiosGet(`post-overview` + "?" + this.postsFilter)
                .then((response) => {
                    if (
                        this.postsFilter === "last_seven_days" ||
                        this.postsFilter === "this_week" ||
                        this.postsFilter === "last_week"
                    ) {
                        this.lineChartLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                    } else if (
                        this.postsFilter === "this_month" ||
                        this.postsFilter === "last_month"
                    ) {
                        this.lineChartLabels = response.data.post_overview[0].map((e, i) => {
                            return i + 1;
                        });
                    } else if (this.postsFilter === "this_year" || this.postsFilter === "total") {
                        this.lineChartLabels = [
                            "Jan",
                            "Feb",
                            "Mar",
                            "Apr",
                            "May",
                            "Jun",
                            "Jul",
                            "Aug",
                            "Sep",
                            "Oct",
                            "Nov",
                            "Dec",
                        ];
                    }

                    this.lineChartData.forEach((element, index) => {
                        element.data = response.data.post_overview[index];
                    });

                    this.publicPost = response.data.public_post; // Total public post
                    this.teamPost = response.data.team_post; // Total team post
                    this.totalPost = response.data.total_post; // Total post
                })
                .finally(() => {
                    this.postOverviewPreloader = false;
                });
        },
    },
    created() {
        if (this.$can("view_posts_overview")) {
            this.postsFilterValue('last_seven_days');
        }
        if (this.$can("view_reaction_overview")) {
            this.getReactions();
        }
        if (this.$can("view_user_summary")) {
            this.getUserCard();
        }
        if (this.$can("view_post_summary")) {
            this.getPostCard();
        }
        if (this.$can("view_top_3_post_creator")) {
            this.getTopPoster();
        }
    }
}
</script>

<style scoped>
.dashboard-badge {
    background-color: var(--base-color) !important;
    color: #4466F2 !important;
    height: 40px !important;
    font-size: 20px;
    padding: 0.52em 0.95em !important;
}

.min-height-315 {
    min-height: 315px !important;
}
</style>